import React from 'react';
import PropTypes from 'prop-types';
import utils from '@utils';
import PaymentHistoryItem from './PaymentHistoryItem';

/**
 * Displays the payments applied to an invoice, including payment history and refunds in transit.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.totals - Totals object containing payment totals.
 * @param {Array<Object>} props.paymentHistory - Array of payment history items.
 * @param {boolean} props.nonrkpayDetailPrepayEnhancementsEnabled - Flag to enable prepay enhancements.
 * @param {boolean} props.showToggle - Whether to show the toggle button for payment history.
 * @param {Function} props.togglePaymentHistory - Function to toggle the payment history display.
 * @param {boolean} props.showPaymentHistory - Whether payment history is currently shown.
 * @param {Array<Object>} props.refundsInTransit - Array of refunds in transit items.
 * @returns {JSX.Element} The rendered PaymentsAppliedDisplay component.
 */
const PaymentsAppliedDisplay = ({
  totals,
  paymentHistory,
  nonrkpayDetailPrepayEnhancementsEnabled,
  showToggle,
  togglePaymentHistory,
  showPaymentHistory,
  refundsInTransit
}) => {
  const { lang } = global;
  const { useMemo } = React;

  const { displayPayments, paymentsTotal } = useMemo(() => {
    if (nonrkpayDetailPrepayEnhancementsEnabled) {
      const filtered = paymentHistory.filter(item => item.Amount > 0);
      return {
        displayPayments: filtered,
        paymentsTotal: totals.payments,
      };
    }
    return {
      displayPayments: paymentHistory,
      paymentsTotal: totals.appliedPayments,
    };
  }, [
    paymentHistory,
    nonrkpayDetailPrepayEnhancementsEnabled,
    totals.payments,
    totals.appliedPayments,
  ]);

  return (
    <div className="row payment-spacer">
	  {showToggle && paymentHistory.length > 0 && (
		<div className="col-xs-1 col-sm-2">
			<button
				type="button"
				className="btn no-padding btn-link btn-block text-center hidden-print"
				onClick={() => togglePaymentHistory()}
			>
				<i
					className={`rkf rkf-chevron-${showPaymentHistory ? "up" : "down"}`}
					aria-label="Toggle payment history."
				/>
			</button>
		</div>
	  )}
	<div className={`${paymentHistory.length > 0 && showToggle ? "col-xs-8 col-sm-7" : "col-xs-9"}`}>
        <strong>{lang.t("Payments Applied")}</strong>
      </div>
      <div className="col-xs-3">
        {utils.currencyFormatter(paymentsTotal)}
      </div>
      <div className={`col-xs-12 ${showPaymentHistory ? "" : "hidden"}`}>
        {displayPayments.map((item, index) => (
          <PaymentHistoryItem
            key={`payment_${item.Id || index}`}
            item={item}
            keyPrefix="payment"
          />
        ))}
		{!nonrkpayDetailPrepayEnhancementsEnabled && (
			refundsInTransit.map((item, index) => (
				<PaymentHistoryItem
					key={`refund_in_transit_${item.Id || index}`}
					item={item}
					keyPrefix="refund_in_transit"
				/>
			))
		)}
      </div>
    </div>
  );
};

PaymentsAppliedDisplay.propTypes = {
  totals: PropTypes.shape({
    payments: PropTypes.number,
    appliedPayments: PropTypes.number
  }).isRequired,
  paymentHistory: PropTypes.array.isRequired,
  refundsInTransit: PropTypes.array.isRequired,
  nonrkpayDetailPrepayEnhancementsEnabled: PropTypes.bool.isRequired,
  showToggle: PropTypes.bool,
  togglePaymentHistory: PropTypes.func,
  showPaymentHistory: PropTypes.bool
};
PaymentsAppliedDisplay.defaultProps = {
  showToggle: false,
  togglePaymentHistory: () => {},
  showPaymentHistory: true
};

export default PaymentsAppliedDisplay;
